import Drawer from '@/components/Drawer';
import { useStoreWatch } from '@/components/Form/useStoreWatch';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import PERMISSIONS from '@/constants/permissions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import {
  createEmailTemplateRequest,
  detachEmailTemplateRequest,
  fetchEmailTemplateDraftRequest,
  saveEmailTemplateDraftRequest,
  updateEmailTemplateRequest,
} from '@/services/api/emailCenter';
import {
  EmailTemplateModel,
  TemplateFragmentModel,
} from '@/typings/api/emailCenter';
import {
  applyServerErrorsToForm,
  extractTwigLineErrors,
  normalizeApiError,
} from '@/utils/apiErrors';
import {
  buildEmailConfigPreviewContext,
  buildThemePreviewContext,
  isAutomationMail,
  isNl2goTemplate,
} from '@/utils/emailCenter';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Form, message, Modal, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import EmailBodyEditor, {
  EmailBodyEditorErrorItem,
  EmailBodyEditorProps,
} from '../../components/EmailBodyEditor';
import { fragmentDisplayName } from '../../components/EmailBodyEditor/labels';
import { parseBinding } from '../../components/EmailBodyEditor/translationBinding';
import { isCompleteHtmlDocument } from '../../components/EmailBodyEditor/twigRenderer';
import ResolvedTranslationHint from '../../components/ResolvedTranslationHint';
import { LANGUAGE_CODES } from '../../constants';
import { resolveTranslations } from '../../translationCatalog';
import { useJourneyTheme } from '../../useJourneyTheme';
import { useSaveTranslation } from '../../useSaveTranslation';
import {
  isFragmentOpenableFromTemplate,
  resolveFragmentDrawerMode,
} from '../Theme/fragmentDrawerMode';
import FragmentEditorDrawer from '../Theme/FragmentEditorDrawer';
import CreateSettingsStep from './CreateSettingsStep';
import CreateStartStep from './CreateStartStep';
import EmailSettingsFields from './EmailSettingsFields';
import SendTestMailDrawer from './SendTestMailDrawer';
import TemplateDrawerFooter from './TemplateDrawerFooter';
import TemplateDrawerNotices from './TemplateDrawerNotices';
import TemplateHistoryDrawer from './TemplateHistoryDrawer';
import useTemplateCreateFlow from './useTemplateCreateFlow';
import useTemplateDraftActions from './useTemplateDraftActions';
import useTemplateDrawerData from './useTemplateDrawerData';
import useTemplateScopeTarget from './useTemplateScopeTarget';

import { useEmailCenterValidateMessages } from '../../useValidateMessages';
import styles from './TemplateDrawer.less';
import { KIND, SENDING_TIME_FORMAT } from './templateFormShape';
import { buildTemplatePayload } from './templatePayload';

dayjs.extend(customParseFormat);

const TemplateDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('template'));
  const templateTypes = useSelector(EmailCenterSelectors.selectTemplateTypes);
  const variables = useSelector(EmailCenterSelectors.selectVariables);
  const translations = useSelector(EmailCenterSelectors.selectTranslations);
  const fragments = useSelector(EmailCenterSelectors.selectFragments);

  /**
   * What the Source view's "insert a building block" picker offers.
   *
   * `core/*` is excluded: those are the visual editor's internal components,
   * placed by the palette with a `_cvn_id` the compiler injects and a manifest
   * the inspector drives. Hand-writing one into the source produces an include
   * the editor cannot address — no id, no props schema — so offering them here
   * invites a broken block. They stay reachable the way they are meant to be,
   * by dragging from the palette in Design.
   */
  const insertableFragments = useMemo(
    () => fragments.filter((fragment) => !fragment.key.startsWith('core/')),
    [fragments],
  );
  const fragmentSources = useSelector(
    EmailCenterSelectors.selectFragmentSources,
  );
  const fragmentTypes = useSelector(EmailCenterSelectors.selectFragmentTypes);
  const fragmentManifests = useSelector(
    EmailCenterSelectors.selectFragmentManifests,
  );
  const fragmentNames = useSelector(EmailCenterSelectors.selectFragmentNames);
  const fragmentOwnership = useSelector(
    EmailCenterSelectors.selectFragmentOwnership,
  );
  const defaultLayoutKey = useSelector(
    EmailCenterSelectors.selectDefaultLayoutKey,
  );

  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const clearGeneralError = useCallback(() => setGeneralError(null), []);
  const [twigErrors, setTwigErrors] = useState<EmailBodyEditorErrorItem[]>([]);
  /**
   * Problems the body editor reports that would stop the email rendering for real
   * recipients — a referenced building block that is not seeded, content placed in
   * a layout block that does not exist, a component missing a required value.
   *
   * The count lives here because saving does: the editor writes into the `body`
   * field, and only the drawer can refuse to submit it. Nothing downstream catches
   * these — a missing include is caught and *logged* at re-render time, so the
   * symptom is a silently stale copy at the provider rather than an error.
   */
  const [bodyIssueCount, setBodyIssueCount] = useState(0);
  const [previewLanguage, setPreviewLanguage] = useState<string>(
    LANGUAGE_CODES[0],
  );
  /**
   * The languages this partner translates into — what the editor's own picker
   * offers, and what the preview defaults to. Unset means all of them.
   */
  const partnerLanguages = useSelector(
    EmailCenterSelectors.selectTranslationLanguages,
  );

  /**
   * Keep the previewed language inside the partner's list.
   *
   * The default is the first supported code, which a partner who does not
   * translate into it would otherwise be stuck previewing — every translated
   * field showing "missing" for a language they deliberately excluded.
   */
  useEffect(() => {
    if (
      partnerLanguages.length &&
      !partnerLanguages.includes(previewLanguage)
    ) {
      setPreviewLanguage(partnerLanguages[0]);
    }
  }, [partnerLanguages, previewLanguage]);
  const record = drawer.record as EmailTemplateModel | null;
  const isEdit = drawer.mode === 'edit' && record != null;
  /**
   * Which view the editor opens on, set by whoever opened the drawer (via
   * `?view=`). Unset leaves the choice to the editor.
   */
  const initialEditorView = drawer.view as EmailBodyEditorProps['defaultView'];

  /**
   * Which create the drawer was opened for, taken from the entry point.
   *
   * `''` is the marketing section's button; anything else is a trigger row. The
   * caller has already decided, so the form states it rather than asking.
   */
  // `record` is already the cast drawer payload; on create it is the partial the
  // caller seeded ({ email_type } or { email_type: '' }).
  const presetEmailType = !isEdit ? record?.email_type ?? null : null;
  const creatingMarketing = presetEmailType === '';
  const creatingForTrigger = !isEdit && !!presetEmailType;
  const triggerType = templateTypes.find(
    (type) => type.key === presetEmailType,
  );
  const triggerLabel = triggerType?.label;

  // The editor re-reports on mount (the drawer is `destroyOnHidden`, so it
  // remounts each time), but a count left from the previously opened template must
  // not block this one in the meantime.
  useEffect(() => {
    setBodyIssueCount(0);
  }, [record?.id]);
  // Mirror of the backend's EmailTemplatePolicy: `draft` accepts draft OR
  // publish holders, a direct save and publishing a draft need `publish`.
  // Test-send has no permission of its own — it goes to the requester, not to a
  // customer, so anyone who can open the template can send themselves a proof.
  const canDraft = useHasAccess([
    PERMISSIONS.EmailTemplate.DRAFT,
    PERMISSIONS.EmailTemplate.PUBLISH,
  ]);
  const canPublish = useHasAccess([PERMISSIONS.EmailTemplate.PUBLISH]);
  const canCreate = useHasAccess([PERMISSIONS.EmailTemplate.CREATE]);
  // Cmd/Ctrl-clicking a block reference opens the fragment editor, which writes
  // through the fragment API — one permission for owned blocks and for copies
  // of standard ones alike.
  const canManageFragments = useHasAccess([PERMISSIONS.EmailFragments.MANAGE]);

  /**
   * Automation mails are viewable, never editable — the automation owns what it
   * sends and when. Derived from the record rather than passed in by the opener,
   * so a deep link (`?template=…`) lands read-only too.
   */
  const isAutomationTemplate = isEdit && isAutomationMail(record);
  // NL2Go names its placeholders differently, so no variable of ours is offered
  // for insertion anywhere in the drawer. Editing the body is unaffected.
  const variablesUnsupported = isNl2goTemplate(record);
  const readOnly =
    isEdit && (record.is_owned === false || !canDraft || isAutomationTemplate);
  /**
   * A master owned by another account: read-only until the user detaches it —
   * detaching clones it under this partner and repoints the partner's journey
   * links, so central updates to the master stop arriving.
   */
  const isForeignMaster = isEdit && record?.is_owned === false;

  const { coveredEventTypes, sendTargetOptions, journeySpecific, scopeTarget } =
    useTemplateScopeTarget({
      form,
      templateTypes,
      presetEmailType,
      recordEmailType: record?.email_type,
      isEdit,
      creatingForTrigger,
      visible: drawer.visible,
    });

  const { theme, journeyHashId } = useJourneyTheme();
  const journeyEmailConfig = useSelector(
    EmailCenterSelectors.selectJourneyEmailConfig,
  );

  const variableGroups = useMemo(() => {
    const groups = variables
      ? Object.entries(variables).map(([namespace, items]) => ({
          namespace,
          items,
        }))
      : [];
    // The journey theme rides along in every email's Twig payload — offer its
    // keys for insertion like any backend-provided variable group.
    const colorKeys = Object.keys(theme?.colors ?? {});
    if (colorKeys.length > 0)
      groups.push({ namespace: 'theme.colors', items: colorKeys });
    const assetKeys = Object.keys(theme?.assets ?? {}).filter(
      (key) => typeof theme?.assets?.[key] === 'string',
    );
    if (assetKeys.length > 0)
      groups.push({ namespace: 'theme.assets', items: assetKeys });
    return groups;
  }, [variables, theme]);

  /**
   * Theme plus the journey's `email_config.template_vars`.
   *
   * The layout reads both — its brand band is `theme.logo`, its footer is
   * `email_config.template_vars.imprint` — so a context with only the theme
   * renders the layout stripped of exactly the chrome the recipient will see.
   */
  const previewContext = useMemo(
    () => ({
      ...buildThemePreviewContext(theme),
      ...buildEmailConfigPreviewContext(journeyEmailConfig?.template_vars),
    }),
    [theme, journeyEmailConfig],
  );

  // Partner translations for the language the preview currently shows. The
  // empty-string rule lives in `translationCatalog`, with the other four readers.
  const editorTransMap = useMemo(
    () => resolveTranslations(translations, previewLanguage),
    [translations, previewLanguage],
  );

  // The editor's ✓ button persists an edited wording partner-wide for the
  // previewed language (same flow as the standalone preview drawer).
  const saveTranslation = useSaveTranslation();
  const handleEditorTranslationSave = useCallback(
    (fullKey: string, value: string) =>
      saveTranslation(fullKey, { [previewLanguage]: value }),
    [saveTranslation, previewLanguage],
  );

  // Grouped for both pickers (the Settings field and the editor's toolbar
  // switch): the partner's own layouts first, the shared ones after.
  const layoutOptions = useMemo(() => {
    const toOption = (fragment: TemplateFragmentModel) => ({
      value: fragment.key,
      label: fragmentDisplayName(fragment),
    });
    const layouts = fragments.filter((fragment) => fragment.type === 'layout');
    const own = layouts.filter((fragment) => fragment.is_owned).map(toOption);
    const shared = layouts
      .filter((fragment) => !fragment.is_owned)
      .map(toOption);
    return [
      ...(own.length
        ? [
            {
              label: getIntlTranslation(intl, {
                id: 'pages.emailCenter.templates.layoutGroup.own',
                defaultMessage: 'Your layouts',
              }),
              options: own,
            },
          ]
        : []),
      ...(shared.length
        ? [
            {
              label: getIntlTranslation(intl, {
                id: 'pages.emailCenter.templates.layoutGroup.public',
                defaultMessage: 'Public layouts',
              }),
              options: shared,
            },
          ]
        : []),
    ];
  }, [fragments, intl]);

  /**
   * The editor toolbar's layout switch writes the same field as the Settings
   * select, so the pick persists on save and the preview follows immediately.
   *
   * `layoutCleared` is what keeps a cleared switch cleared: an empty
   * `layout_key` is also how a template that never chose a layout looks, and that
   * is precisely the case the partner-default fallback below exists for — so
   * without the flag the fallback would put the default layout straight back and
   * the pick would look like it did not take.
   */
  const [layoutCleared, setLayoutCleared] = useState(false);
  const handleLayoutChange = useCallback(
    (key: string | null) => {
      setLayoutCleared(key === null);
      form.setFieldsValue({ layout_key: key });
    },
    [form],
  );

  /**
   * Watched rather than read from `record`: a key minted while creating a
   * template should be named after the name being typed, not after nothing.
   *
   * `useStoreWatch`, not `Form.useWatch`, because most of these are not visible
   * to a plain watch: `layout_key` has no `Form.Item` at all (the editor's
   * toolbar writes it straight into the store), and every settings field is
   * unmounted while the editor is on the canvas. Both read as `undefined`
   * otherwise — which is how a template with its own layout ended up
   * previewing under the partner default one.
   */
  const watchedName = useStoreWatch<string>('name', form);
  const watchedKind = useStoreWatch('kind', form);
  const watchedEmailType = useStoreWatch<string | undefined>(
    'email_type',
    form,
  );
  const watchedLayoutKey = useStoreWatch<string | null>('layout_key', form);
  const watchedSubject = useStoreWatch('subject', form);
  const watchedPretext = useStoreWatch('pretext', form);
  const watchedBody = useStoreWatch('body', form);
  // UI-only opt-in in front of the two sender fields (see the sender section).
  const watchedSenderOverride = useStoreWatch('sender_override', form);

  // Server parity (EmailTemplate::renderEmail): an explicit choice wins — a
  // layout, or the switch cleared to none; the partner default only wraps bodies
  // that are not already complete HTML documents (an {% extends %} would erase
  // those).
  const effectiveLayoutKey =
    watchedLayoutKey ||
    (layoutCleared ||
    isCompleteHtmlDocument(typeof watchedBody === 'string' ? watchedBody : '')
      ? null
      : defaultLayoutKey) ||
    null;

  /**
   * The resolved-value strip, only for a **mixed** field.
   *
   * A field whose whole value is one key is handled by the control itself, which
   * shows the wording inline and edits it per language — rendering this as well
   * would read out the same text twice. A mixed value
   * (`{{ customer.firstname }}, {{ 'x'|trans }}`) has no single binding, so the strip
   * is still the only thing that says what recipients will see.
   */
  const mixedHint = useCallback(
    (source: unknown) =>
      parseBinding(source).kind === 'bound' ? null : (
        <ResolvedTranslationHint
          source={source}
          translations={editorTransMap}
          onSave={handleEditorTranslationSave}
        />
      ),
    [editorTransMap, handleEditorTranslationSave],
  );
  // Memoized rather than simply derived: both feed `emailSettingsFields`, whose
  // whole point below is holding one identity across renders — a hint rebuilt
  // every keystroke would undo that on the way in.
  const mixedSubjectHint = useMemo(
    () => mixedHint(watchedSubject),
    [mixedHint, watchedSubject],
  );
  const mixedPretextHint = useMemo(
    () => mixedHint(watchedPretext),
    [mixedHint, watchedPretext],
  );

  /**
   * What the create step's inbox mock shows: a bound field resolves to the
   * preview language's wording, a literal (or mixed) value reads as typed.
   */
  const inboxPreviewText = useCallback(
    (source: unknown) => {
      const binding = parseBinding(source);
      return binding.kind === 'bound'
        ? editorTransMap[binding.key] ?? ''
        : binding.text;
    },
    [editorTransMap],
  );
  const inboxSubject = inboxPreviewText(watchedSubject);
  const inboxPretext = inboxPreviewText(watchedPretext);

  useTemplateDrawerData(drawer.visible, journeyHashId);

  /**
   * Fill the form for whatever the drawer was opened on.
   *
   * Deliberately keyed on `[drawer.visible, record?.id]` alone: this is a
   * *seeding* effect, and everything else it reads (`templateTypes`,
   * `scopeTarget`, the form itself) is whatever is known at the moment of
   * opening. Re-running it on any of those would overwrite what the user has
   * since typed, which is why exhaustive-deps is off rather than satisfied.
   */
  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);
    setTwigErrors([]);
    // Per template: the flag answers "did *this* one's switch get cleared".
    setLayoutCleared(false);
    if (isEdit && record) {
      const fill = (values: Partial<EmailTemplateModel>) => {
        const next = { ...record, ...values };
        form.setFieldsValue({
          ...next,
          kind: record.email_type === '' ? KIND.MARKETING : KIND.TRANSACTIONAL,
          sending_time: values.sending_time
            ? dayjs(values.sending_time, SENDING_TIME_FORMAT)
            : undefined,
          // The API has no "overrides the sender" flag — carrying either field
          // *is* the override, so the toggle is derived from them on open.
          sender_override: Boolean(
            next.sender_from_name || next.sender_from_email,
          ),
        });
        // Same commit as the body above — see `handleDesignDocumentChange`.
        setDesignDocument(next.design_document ?? null);
      };

      fill(record);

      /**
       * A draft, when there is one, is what you edit.
       *
       * The list and `record` carry the *published* values — that is the point
       * of a draft, and what keeps the template sending its current version.
       * But opening the editor to anything other than your own unpublished work
       * means every session starts by discarding it, so the draft is fetched and
       * laid over the published values here.
       *
       * Fetched rather than carried on the list payload: bodies are the bulk of
       * a template, and every row would pay for them to serve the few that have
       * a draft.
       */
      if (record.has_draft) {
        setDraftLoading(true);
        fetchEmailTemplateDraftRequest({ params: { uuid: record.id } })
          .then((result) =>
            processAPIResponseNow(
              result,
              {
                onSuccess: (draft) => {
                  if (draft) fill(draft as Partial<EmailTemplateModel>);
                },
              },
              'data',
            ),
          )
          .finally(() => setDraftLoading(false));
      }
    } else {
      form.resetFields();
      setDesignDocument(null);
      resetCreateFlow();
      // Create opened from an uncovered trigger row ('' = the marketing
      // section's create) — preselect that trigger / the marketing kind.
      const presetEmailType = (
        drawer.record as Partial<EmailTemplateModel> | null
      )?.email_type;
      if (presetEmailType != null) {
        /**
         * A transactional template is named after the event it answers.
         *
         * The name is required by the API and shown wherever a template is
         * listed, but for a trigger-bound one it carries no information the
         * trigger does not already give — "Appointment Confirmed" *is* the
         * answer. Asking for it made the first step of creating an email a
         * question with one sensible answer. It stays editable afterwards.
         */
        const triggerLabel = templateTypes.find(
          (type) => type.key === presetEmailType,
        )?.label;

        form.setFieldsValue(
          presetEmailType === ''
            ? { kind: KIND.MARKETING, email_type: '' }
            : {
                kind: KIND.TRANSACTIONAL,
                email_type: presetEmailType,
                name: triggerLabel ?? presetEmailType,
                // The reset above wiped what the preselect effect set — the
                // same rule, applied to whatever coverage is known right now.
                journey_hash_id: scopeTarget,
              },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  /**
   * Whether the editor has an edit that closing would throw away.
   *
   * From the editor's undo stack (`onDirtyChange`), not from the form: the body is
   * written with `setFieldsValue`, which antd does not count as touched, so
   * `isFieldsTouched()` reports a pristine form over an afternoon of layout work.
   */
  const [bodyDirty, setBodyDirty] = useState(false);
  useEffect(() => {
    if (!drawer.visible) setBodyDirty(false);
  }, [drawer.visible]);

  /**
   * Close without asking — for the closes that *follow* a completed action
   * (saved, published, discarded, restored), where there is by definition
   * nothing left to lose. The flag is cleared with it: the drawer can be
   * reopened before the editor unmounts, and a stale `true` would then guard a
   * document nobody has touched.
   */
  const closeNow = useCallback(() => {
    setBodyDirty(false);
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'template' },
    });
  }, [dispatch]);

  /**
   * Closing with unsaved work asks first.
   *
   * The ✕ used to discard the document silently — one mistaken click and the design
   * was gone, with no draft kept and no way back. `Modal.confirm` rather than a
   * `Popconfirm` because the trigger belongs to the shared drawer header, which has
   * no anchor to hang a popover on (docs/drawers.md allows the confirm dialog form).
   */
  const close = useCallback(() => {
    if (!bodyDirty) {
      closeNow();
      return;
    }
    Modal.confirm({
      title: getIntlTranslation(intl, {
        id: 'pages.users.drawer.discardConfirm',
        defaultMessage:
          'You have unsaved changes, do you want to discard them?',
      }),
      okText: getIntlTranslation(intl, {
        id: 'common.actions.discard',
        defaultMessage: 'Discard',
      }),
      okType: 'danger',
      cancelText: getIntlTranslation(intl, {
        id: 'common.actions.cancel',
        defaultMessage: 'Cancel',
      }),
      onOk: closeNow,
    });
  }, [bodyDirty, closeNow, intl]);

  // A fragment reference lives in the Theme tab, not this drawer — close the
  // template editor and deep-link the Theme tab to open that block.
  /**
   * Editing a building block **stacks a drawer over this one**.
   *
   * It used to close the template drawer and navigate to the Theme tab — which threw
   * away where you were and what you were doing, to change a partial you were only
   * looking at because of this email. `FragmentEditorDrawer` is a local, stackable
   * component (the Theme tab mounts a stack of them itself), so it mounts here too and
   * this drawer stays exactly as it was underneath.
   */
  const [editingFragmentKey, setEditingFragmentKey] = useState<string | null>(
    null,
  );
  /**
   * Opens a block's details — the partner's own blocks only. Opening a standard
   * one could only ever save a copy under a new key, which this email does not
   * include: a change that is in the database and has no visible effect. The
   * toast names the Theme tab, where that copy is made on purpose
   * ({@see isFragmentOpenableFromTemplate}).
   */
  const handleOpenFragment = useCallback(
    (key: string) => {
      const fragment = fragments.find((item) => item.key === key) ?? null;
      if (isFragmentOpenableFromTemplate(fragment)) {
        setEditingFragmentKey(key);
        return;
      }
      // Silent for the component library: those blocks are named by the palette
      // and were never presented as editable anywhere.
      if (!fragment || fragment.key.startsWith('core/')) return;
      message.info(
        getIntlTranslation(intl, {
          id: 'pages.emailCenter.templates.standardBlockNotEditable',
          defaultMessage:
            '“{name}” is a standard building block, so it cannot be edited from an email. Copy it in the Theme tab to make a version of your own, then place that copy here.',
          values: { name: fragmentDisplayName(fragment) },
        }),
      );
    },
    [fragments, intl],
  );

  const editingFragment = useMemo(() => {
    const fragment =
      fragments.find((item) => item.key === editingFragmentKey) ?? null;
    // The ownership rule again at the mount, not just at the click: the catalog
    // is refetched after every fragment save, so the row can change underneath.
    if (!isFragmentOpenableFromTemplate(fragment)) return null;
    /**
     * Saving from that drawer goes through the fragment API. Without
     * `components_manage` it could only ever end in a 403, so it does not open,
     * the same reason the Layout tab hides those actions.
     */
    return canManageFragments ? fragment : null;
  }, [fragments, editingFragmentKey, canManageFragments]);

  /** A saved partial changes what the canvas renders, so the catalog must be refetched. */
  const handleFragmentSaved = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.FETCH_FRAGMENTS,
      payload: { force: true },
    });
  }, [dispatch]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [testMailOpen, setTestMailOpen] = useState(false);
  const { draftBusy, draftLoading, setDraftLoading, runDraftAction } =
    useTemplateDraftActions({ templateId: record?.id, close: closeNow });

  /**
   * The editor's write-back for the design document — into the form store, so
   * `getFieldsValue(true)` carries whichever document the editor last emitted,
   * *and* into state, which is what the editor is actually fed.
   *
   * **Why it cannot be read back off the store.** A watch is notified in a macro
   * task, while `body` — a real `Form.Item` — re-renders in the same commit as
   * `setFieldsValue`. The editor decides what a template *is* from the body it is
   * handed (`loadDocument`), so a document that arrives a tick later arrives after
   * that decision: the saved Twig was read as one legacy blob, which locks the
   * structure and forces the Source view. That is the whole bug — a template
   * designed visually re-opened as source-only. State makes the two halves land
   * in the same commit.
   */
  const [designDocument, setDesignDocument] = useState<unknown>(null);
  const handleDesignDocumentChange = useCallback(
    (design: unknown) => {
      setDesignDocument(design ?? null);
      form.setFieldsValue({ design_document: design ?? null });
    },
    [form],
  );

  /**
   * Save.
   *
   * `asDraft` stages the edit instead of publishing it: the template keeps
   * sending its current version until someone publishes the draft. Publishing
   * stays the default action, so the button that was always there behaves as it
   * always did — staging is the deliberate choice.
   */
  const handleSave = useCallback(
    async (asDraft = false) => {
      /**
       * The rejection has to be caught here.
       *
       * `validateFields` rejects with AntD's error-fields **object**, not an Error,
       * and the footer calls this from an `onClick` that drops the promise — so a
       * blocked save became an unhandled rejection and a `PAGEERROR Object` in
       * Sentry on every click. Nothing changed on screen either: the body field's
       * message was already standing there from before the click, so the button
       * read as dead. The toast is the acknowledgement, and it reuses the field's
       * own wording rather than inventing a second phrasing for one problem.
       */
      try {
        await form.validateFields();
      } catch {
        message.error(
          getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.bodyHasBlockingIssues',
            defaultMessage:
              'Fix the problems listed above the email before saving.',
          }),
        );
        return;
      }
      /**
       * `getFieldsValue(true)` — every value in the store, not just the mounted ones.
       *
       * `validateFields()` resolves with only the fields currently rendered, and
       * several deliberately are not: creating hides name, type and event because
       * the entry point already fixed them, and the settings panel unmounts
       * whenever the editor is on any view but Settings. Submitting its return
       * value posted a payload missing `name`, `subject`, `pretext` and
       * `email_type` — the server answered 422 and the template was never created.
       *
       * The values are all present in the form store regardless; `true` is what
       * asks for them.
       */
      /**
       * The shaping rules — UI-only fields dropped, marketing-only
       * `sending_time`, the sender override as explicit nulls, and the create's
       * `journey_id` — live in `templatePayload`, where they can be read and
       * tested without mounting an editor to reach the Save button.
       */
      const { payload, createPayload, draftPayload } = buildTemplatePayload({
        values: form.getFieldsValue(true),
        isEdit,
        scopeTarget,
      });

      setSaving(true);
      setGeneralError(null);
      setTwigErrors([]);

      const result = asDraft
        ? await saveEmailTemplateDraftRequest({
            params: { uuid: record!.id },
            payload: draftPayload,
          })
        : isEdit
        ? await updateEmailTemplateRequest({
            params: { uuid: record!.id },
            payload,
          })
        : await createEmailTemplateRequest({ payload: createPayload });
      setSaving(false);

      processAPIResponseNow(
        result,
        {
          // Backend quirk: creates answer HTTP 200 with message "201" — status
          // keying inside the helper is what makes this correct.
          showSuccessMessage: true,
          successEntity: INTL_ENTITY.EMAIL_TEMPLATE,
          onSuccess: () => {
            /**
             * The template saved but never reached the email provider.
             *
             * A partial success the generic toast cannot express: the row
             * exists, so this is not an error, but nothing will send until the
             * provider object is there. The envelope says so with a flag rather
             * than in `message`, which the success toast overwrites.
             */
            const envelope = result as
              | { data?: { provider_synced?: boolean } }
              | undefined;

            if (envelope?.data?.provider_synced === false) {
              message.warning(
                getIntlTranslation(intl, {
                  id: 'pages.emailCenter.templates.create.providerSyncFailed',
                  defaultMessage:
                    'Saved, but not yet created in the email provider — it will be retried the next time you save.',
                }),
              );
            }
            /**
             * A draft save keeps the editor open. Closing meant the URL's
             * `?template=` immediately reopened the drawer from the template pool
             * — as it stood *before* the refetch — so the draft banner and its
             * publish/discard actions did not appear until a manual reload. The
             * record is patched in place instead, which is also the behaviour the
             * button describes: staging an edit, not finishing one.
             */
            if (asDraft) {
              // The document is on the server now, so it is not unsaved work
              // any more — the next real edit re-raises the flag from the
              // editor's own undo stack.
              setBodyDirty(false);
              dispatch({
                type: EMAIL_CENTER_ACTIONS.PATCH_DRAWER_RECORD,
                payload: { drawer: 'template', record: { has_draft: true } },
              });
            } else {
              /**
               * `closeNow`, never `close`.
               *
               * The guarded close asked "discard your unsaved changes?" *after*
               * the save had succeeded — the changes were saved, and the
               * question was about them. Answering Cancel then left the drawer
               * standing on a template that already existed: in create mode the
               * next Save posted a second one, and the drawer went on presenting
               * itself as a creation that had in fact already happened.
               */
              closeNow();
            }
            dispatch({
              type: EMAIL_CENTER_ACTIONS.FETCH_TEMPLATES,
              payload: {},
            });
          },
          onError: (data) => {
            const normalized = normalizeApiError(
              data,
              getIntlTranslation(intl, {
                id: 'pages.common.serverError',
                defaultMessage: 'There was an error processing your request.',
              }),
            );
            const lineErrors = extractTwigLineErrors(normalized);
            setTwigErrors(lineErrors);
            applyServerErrorsToForm(form, normalized.fieldErrors, {
              onUnmapped: (key, messages) =>
                setGeneralError(
                  (prev) => prev ?? `${key}: ${messages.join(' ')}`,
                ),
            });
            // Show the headline unless it is already itemized under the editor.
            if (
              lineErrors.length === 0 &&
              Object.keys(normalized.fieldErrors).length === 0
            ) {
              setGeneralError(normalized.message);
            }
            /**
             * The failure said out loud, on top of wherever its details landed.
             *
             * The details land in three different places — itemized in the
             * editor, on the offending form field, or in the alert above — and
             * two of them are reachable only from a view the user may not be
             * on: field errors sit in Settings, which unmounts while the canvas
             * is up. Someone who pressed Save has to learn that it failed from
             * the press itself, not from finding the view that says so.
             */
            message.error(normalized.message);
          },
        },
        'data',
      );
    },
    [form, isEdit, record, scopeTarget, closeNow, dispatch, intl],
  );

  /**
   * The event the new template answers, as the base picker should filter on.
   *
   * Watched rather than read off `record`: the create entry points seed the
   * form, and the form is what the save sends. `''` (marketing) is a real
   * value here, so the fallback has to be `??`, never `||`.
   */
  const baseEmailType = watchedEmailType ?? presetEmailType;

  /**
   * The three create steps and the starting-point copy. `onBaseApplied` is
   * `setDesignDocument` on purpose: a copied base has to reach the editor as
   * blocks in the same commit as its body — the whole point of that state.
   */
  const {
    createStep,
    setCreateStep,
    createBase,
    setCreateBase,
    baseTemplateId,
    setBaseTemplateId,
    baseTemplateOptions,
    onCreateSettings,
    onCreateStart,
    onCreatePanel,
    goToStartChoice,
    startDesigning,
    resetCreateFlow,
  } = useTemplateCreateFlow({
    form,
    isEdit,
    baseEmailType,
    onLeaveStep: clearGeneralError,
    onBaseApplied: setDesignDocument,
  });

  /**
   * Detach a foreign master: the backend clones it under this partner and
   * repoints the partner's journey links. The drawer then swaps onto the clone
   * (the record id changes, so the form and editor reload for it) — the user
   * continues editing their own copy in place.
   */
  const [detaching, setDetaching] = useState(false);
  const handleDetach = useCallback(async () => {
    if (!record) return;
    setDetaching(true);
    const result = await detachEmailTemplateRequest({
      params: { uuid: record.id },
    });
    setDetaching(false);
    processAPIResponseNow(
      result,
      {
        onSuccess: (clone: EmailTemplateModel) => {
          if (!clone) return;
          message.success(
            getIntlTranslation(intl, {
              id: 'pages.emailCenter.templates.detachSuccess',
              defaultMessage:
                'Copy created — you are now editing your own version.',
            }),
          );
          dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_TEMPLATES, payload: {} });
          dispatch({
            type: EMAIL_CENTER_ACTIONS.OPEN_DRAWER,
            payload: { drawer: 'template', mode: 'edit', record: clone },
          });
        },
      },
      'data',
    );
  }, [record, dispatch, intl]);

  /**
   * The template's own attributes, rendered *inside* the editor rather than in a
   * form above it, so the drawer is editor-only (plan: email-editor-unified, A2).
   *
   * While creating they are the whole of step 1 instead, mounted in the panel
   * below — one place at a time, or two `Form.Item`s would share each name.
   *
   * They stay `Form.Item`s of the drawer's `Form`: relocating the DOM is all that
   * is wanted, and moving them out would mean re-implementing `validateFields`,
   * the existing rules and the body validator that blocks a broken save.
   *
   * **Memoized because it is a prop of a memoized editor.** `VisualEditorBody` is
   * `React.memo`, and every other thing this drawer hands it is stable — reselect
   * selectors, `useMemo`, `useCallback`. This element was the one exception, minted
   * fresh on each render, so the memo never held. And the drawer re-renders on every
   * keystroke (`useStoreWatch` on name/subject/pretext/body), which meant typing on
   * the canvas re-rendered the whole three-pane editor — palette, canvas, inspector,
   * each individually memoized and each bypassed from up here.
   */
  /**
   * The template's attributes, as an element rather than a rendered component.
   *
   * `VisualEditorBody` is `React.memo` and this is its `settingsSlot`; every
   * other prop the drawer hands it is stable (reselect selectors, `useMemo`,
   * `useCallback`), so a slot minted fresh on each render was the one thing
   * keeping that memo from ever holding. And the drawer re-renders on every
   * keystroke — `useStoreWatch` on name/subject/pretext/body — which meant
   * typing on the canvas re-rendered palette, canvas and inspector alike.
   */
  const emailSettingsFields = useMemo(
    () => (
      <EmailSettingsFields
        form={form}
        readOnly={readOnly}
        isEdit={isEdit}
        creatingMarketing={creatingMarketing}
        onCreateSettings={onCreateSettings}
        templateTypes={templateTypes}
        coveredEventTypes={coveredEventTypes}
        mixedSubjectHint={mixedSubjectHint}
        mixedPretextHint={mixedPretextHint}
        previewLanguage={previewLanguage}
        templateName={watchedName}
        kind={watchedKind}
        senderOverride={watchedSenderOverride}
        variableGroups={variableGroups}
        variablesUnsupported={variablesUnsupported}
        journeyEmailConfig={journeyEmailConfig}
      />
    ),
    [
      readOnly,
      isEdit,
      creatingMarketing,
      onCreateSettings,
      templateTypes,
      coveredEventTypes,
      mixedSubjectHint,
      mixedPretextHint,
      previewLanguage,
      watchedName,
      watchedKind,
      watchedSenderOverride,
      variableGroups,
      variablesUnsupported,
      journeyEmailConfig,
      form,
    ],
  );

  /**
   * Only on step 1 of creating, where Enter means "Next" over a column of plain
   * fields. In the editor it would mean saving the whole email from whichever
   * field the caret happened to be in — a workspace, not a dialog to confirm.
   */
  const submitOnEnter = useSubmitOnEnter(
    onCreateSettings ? goToStartChoice : undefined,
  );

  return (
    <>
      <Drawer
        /**
         * The drawer is as wide as its contents need, which is two very
         * different things.
         *
         * The editor is a three-pane layout (palette / canvas / inspector), so it
         * takes the viewport minus a 100px gutter — a fixed 1280px squeezed the
         * canvas on wide screens, and the gutter keeps the page behind visible as
         * the affordance for clicking out.
         *
         * Step 1 of creating is a single narrow column of fields. Opening a sheet
         * across the whole screen to show it left the form marooned in the middle
         * of an empty panel, so it gets a width that matches what is in it.
         */
        width={
          onCreatePanel
            ? 'min(960px, calc(100vw - 48px))'
            : 'calc(100vw - 100px)'
        }
        classNames={{ body: styles.drawerAntBody }}
        drawerBodyClassName={styles.editorDrawerBody}
        open={drawer.visible}
        onOpenChange={close}
        title={
          <Space className={styles.titleRow} size={12}>
            {readOnly ? (
              record?.name
            ) : isEdit ? (
              <F
                entity={INTL_ENTITY.EMAIL_TEMPLATE}
                action={INTL_ACTION.EDIT}
              />
            ) : creatingForTrigger ? (
              /* The trigger is the subject of this drawer, so it is the title —
               it also stands in for the Event field the form no longer asks. */
              <F
                id="pages.emailCenter.templates.create.titleForTrigger"
                defaultMessage="New email for “{trigger}”"
                values={{ trigger: triggerLabel ?? '' }}
              />
            ) : (
              <F
                id="pages.emailCenter.templates.create.titleMarketing"
                defaultMessage="New automation email"
              />
            )}
          </Space>
        }
        footer={
          <TemplateDrawerFooter
            isEdit={isEdit}
            readOnly={readOnly}
            onCreateSettings={onCreateSettings}
            onCreateStart={onCreateStart}
            canPublish={canPublish}
            saving={saving}
            draftLoading={draftLoading}
            onOpenHistory={() => setHistoryOpen(true)}
            onOpenTestMail={() => setTestMailOpen(true)}
            onNext={goToStartChoice}
            onBack={setCreateStep}
            onStartDesigning={startDesigning}
            startDisabled={createBase === 'template' && !baseTemplateId}
            onSave={handleSave}
          />
        }
      >
        <TemplateDrawerNotices
          record={record}
          isEdit={isEdit}
          readOnly={readOnly}
          isAutomationTemplate={isAutomationTemplate}
          isForeignMaster={isForeignMaster}
          canCreate={canCreate}
          canPublish={canPublish}
          detaching={detaching}
          onDetach={handleDetach}
          draftBusy={draftBusy}
          onDraftAction={runDraftAction}
          generalError={generalError}
        />

        {/**
         * No `disabled={readOnly}` on the form.
         *
         * It reads as the safe default, but antd's `disabled` is a context that
         * reaches *every* control below it — including, since the fields moved inside
         * the editor, two things read-only was never meant to touch: the preview's
         * sample data (values for one preview, saved nowhere) and the translation
         * controls (partner-wide wording, shared with every other email that uses the
         * key). Neither is an edit of this template.
         *
         * So read-only is asserted where it belongs instead: `emailSettingsFields`
         * wraps the template's own attributes in that context, and the editor gates
         * blocks, properties, the source, undo/redo and the layout switch off its
         * own `readOnly` prop.
         */}
        <Form
          {...submitOnEnter}
          validateMessages={validateMessages}
          form={form}
          layout="vertical"
          className={styles.editorForm}
        >
          {onCreateSettings && (
            <CreateSettingsStep
              creatingForTrigger={creatingForTrigger}
              journeySpecific={journeySpecific}
              triggerDescription={triggerType?.description}
              sendTargetOptions={sendTargetOptions}
              settingsFields={emailSettingsFields}
              subject={inboxSubject}
              pretext={inboxPretext}
            />
          )}

          {onCreateStart && (
            <CreateStartStep
              base={createBase}
              onBaseChange={setCreateBase}
              baseTemplateId={baseTemplateId}
              onBaseTemplateChange={setBaseTemplateId}
              baseTemplateOptions={baseTemplateOptions}
            />
          )}

          <Form.Item
            name="body"
            hidden={onCreatePanel}
            className={styles.bodyItem}
            rules={[
              { required: true },
              {
                // Blocks the save rather than the edit: the editor still shows what
                // is wrong, and the body in the field still matches what is on screen.
                validator: () =>
                  bodyIssueCount > 0
                    ? Promise.reject(
                        new Error(
                          getIntlTranslation(intl, {
                            id: 'pages.emailCenter.templates.bodyHasBlockingIssues',
                            defaultMessage:
                              'Fix the problems listed above the email before saving.',
                          }),
                        ),
                      )
                    : Promise.resolve(),
              },
            ]}
          >
            {/*
             * Mounted only once the body it should open with is final.
             *
             * The editor builds its document at mount from `value`. When a draft
             * exists that value arrives asynchronously, so mounting first with the
             * published body opened a window where `value` was already the draft
             * while the document was still the published one — and the divergence
             * check reads exactly that pair, so it concluded the body had been
             * hand-edited and raised "edited as code, the visual version is out of
             * date" on a template only ever touched in the visual editor.
             *
             * Verified the stored data was innocent first: the saved draft
             * round-trips `fidelity: exact` with zero compile warnings and
             * `bodiesDiverge: false` (scripts/diagnose-detach.ts). The window was
             * the whole bug, so closing it is the fix rather than relaxing the
             * check — which would have hidden real hand edits too.
             */}
            {onCreatePanel ? (
              /* Steps 1+2 render their panels above, outside this item. The
               editor is not mounted at all — an empty three-pane canvas behind a
               form is scenery, and mounting it would start the fragment/label
               fetches for a template that may never exist. */
              <span />
            ) : draftLoading ? (
              <div className={styles.editorLoading}>
                <Spin />
              </div>
            ) : (
              <EmailBodyEditor
                /* Remount on record swap — detaching replaces the foreign
                   master with the partner's clone while the drawer stays
                   open, and the editor builds its document at mount. */
                key={record?.id ?? 'create'}
                /* The stored design document rides the form store like every
                   other saved field (fill() spreads the record/draft in), so
                   the editor opens on it and the save payload carries what
                   the editor last emitted. */
                designJson={designDocument}
                onDesignJsonChange={handleDesignDocumentChange}
                variableGroups={variableGroups}
                readOnly={readOnly}
                errors={twigErrors}
                previewContext={previewContext}
                translations={editorTransMap}
                fragments={fragmentSources}
                fragmentTypes={fragmentTypes}
                fragmentManifests={fragmentManifests}
                fragmentNames={fragmentNames}
                fragmentOwnership={fragmentOwnership}
                layoutKey={effectiveLayoutKey}
                layoutOptions={layoutOptions}
                onLayoutChange={handleLayoutChange}
                defaultView={initialEditorView}
                previewLanguage={previewLanguage}
                languageOptions={partnerLanguages}
                onPreviewLanguageChange={setPreviewLanguage}
                onTranslationSave={handleEditorTranslationSave}
                onDirtyChange={setBodyDirty}
                insertableFragments={insertableFragments}
                onOpenFragment={handleOpenFragment}
                onBlockingIssuesChange={setBodyIssueCount}
                settingsSlot={emailSettingsFields}
                templateName={watchedName}
                variablesUnsupported={variablesUnsupported}
                /* The kind as the form currently holds it, not as the record
                   was loaded: a mail being created is switched between the two
                   in step 1, and the toolbar has to follow that pick. */
                isMarketing={watchedKind === KIND.MARKETING}
              />
            )}
          </Form.Item>
        </Form>
      </Drawer>

      {testMailOpen && record && (
        <SendTestMailDrawer
          uuid={record.id}
          variables={previewContext}
          language={previewLanguage}
          hasDraft={record.has_draft}
          onClose={() => setTestMailOpen(false)}
        />
      )}

      {historyOpen && record && (
        <TemplateHistoryDrawer
          uuid={record.id}
          templateName={record.name}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => {
            // A restore rewrites the template's fields, so what this drawer is
            // showing is now stale — close it rather than present old values.
            // Unguarded: the restore is the deliberate discard of what is on
            // screen, so asking about it again would only be a second dialog
            // for the choice just made.
            closeNow();
            dispatch({
              type: EMAIL_CENTER_ACTIONS.FETCH_TEMPLATES,
              payload: {},
            });
          }}
        />
      )}

      {/* Stacked above, so this template drawer keeps its state and its scroll. */}
      {editingFragment && (
        <FragmentEditorDrawer
          // Always a real edit: only owned blocks get this far, so the copy flow
          // (which would leave this email's `{% include %}` on the original) cannot
          // be reached from here. Resolved anyway, to keep one decision point.
          mode={resolveFragmentDrawerMode('edit', editingFragment)}
          record={editingFragment}
          fragments={fragments}
          fragmentSources={fragmentSources}
          translations={translations}
          onClose={() => setEditingFragmentKey(null)}
          onSaved={handleFragmentSaved}
          onOpenReference={handleOpenFragment}
          showBack
        />
      )}
    </>
  );
};

export default TemplateDrawer;
